import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSnackbar } from '@mysaiva/core'
import { safeLocalStorageGet, safeLocalStorageSet } from '@mysaiva/core/utils'
import { SOURCE_QUERY_PARAM } from '../constants'
import { resolveWorkspaceLocation, workspacePath } from '../navigation'
import { DeveloperPlatformEvent, trackDeveloperPlatformEvent } from '../analytics/events'

export const MIGRATION_NOTICE_PARAM = 'notice'
export const MIGRATION_NOTICE_VALUE = 'api-moved'
export const MIGRATION_NOTICE_STORAGE_KEY = 'mysaiva-dp-api-moved-notice'

/**
 * One-time "API access has moved to SAIVA Developers" notice (Platform-7,
 * SAIV-11353). UPT redirects its legacy `/upt/integrations/*` routes here
 * with `?notice=api-moved&from=upt`; the notice shows once per browser,
 * records the redirect hit for analytics and strips the marker from the URL.
 */
export function MigrationNotice() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const handled = useRef(false)

  const params = new URLSearchParams(location.search)
  const isMarked = params.get(MIGRATION_NOTICE_PARAM) === MIGRATION_NOTICE_VALUE

  useEffect(() => {
    if (!isMarked || handled.current) return
    handled.current = true

    const search = new URLSearchParams(location.search)
    const sourceProduct = search.get(SOURCE_QUERY_PARAM) ?? undefined
    search.delete(MIGRATION_NOTICE_PARAM)
    const rest = search.toString()
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`, { replace: true })

    trackDeveloperPlatformEvent(DeveloperPlatformEvent.legacyRedirect, {
      ...(sourceProduct ? { source_product: sourceProduct } : {}),
      path: location.pathname
    })

    if (safeLocalStorageGet(MIGRATION_NOTICE_STORAGE_KEY) === 'dismissed') return
    safeLocalStorageSet(MIGRATION_NOTICE_STORAGE_KEY, 'dismissed')

    const onApiKeys = resolveWorkspaceLocation(location.pathname).page.id === 'api-keys'
    snackbar.info(t('developerPlatform.migration.apiMoved'), {
      duration: null,
      action: onApiKeys
        ? undefined
        : {
            label: t('developerPlatform.migration.openApiKeys'),
            onClick: () => navigate(workspacePath('api-keys'))
          }
    })
  }, [isMarked, location.search, location.pathname, location.hash, navigate, snackbar, t])

  return null
}
